import { useState, useEffect } from "react";
import { Visit } from "@visitproof/types";
import { useAccount } from "../../../contexts/AccountContext";
import { useScheduler } from "../../../contexts/SchedulerContext";
import {
  collection,
  onSnapshot,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase";
import moment from "moment-timezone";
import { DateRange } from "../types";

export const useSchedulerData = () => {
  const { account } = useAccount();
  const { filter, calendarView } = useScheduler();
  
  const [date, setDate] = useState(new Date());
  const [range, setRange] = useState<DateRange | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [filteredVisits, setFilteredVisits] = useState<Visit[]>([]);
  const [updatingEvent, setUpdatingEvent] = useState<Visit | null>(null);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Bumping this re-runs the Firestore subscription below, which re-emits the
  // authoritative visit list. Used to snap a drag-and-drop event back to its
  // saved position when the update is dismissed without being persisted.
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => setRefreshKey((k) => k + 1);

  // Initialize range based on calendar view
  useEffect(() => {
    if (!range) {
      let start: Date, end: Date;
      const now = new Date();
      
      if (calendarView === "day") {
        start = end = now;
      } else if (calendarView === "week") {
        start = moment(now).startOf("week").toDate();
        end = moment(now).endOf("week").toDate();
      } else {
        start = moment(now).startOf("month").toDate();
        end = moment(now).endOf("month").toDate();
      }
      
      setRange({ start, end });
    }
  }, [calendarView, range]);

  // Filter visits based on current filter
  useEffect(() => {
    setFilteredVisits(
      visits.filter((visit) => {
        if (filter?.caseId && visit.caseId !== filter.caseId) return false;
        if (filter?.supervisorId && visit.supervisorId !== filter.supervisorId) return false;
        return true;
      })
    );
  }, [filter, visits]);

  // Fetch visits from Firestore
  useEffect(() => {
    if (!range || !account?.id) return;

    const start = range.start;
    const end = range.end;

    setError(null);

    const unsub = onSnapshot(
      query(
        collection(db, "visits"),
        where("accountId", "==", account.id),
        where("startDate", ">=", Timestamp.fromDate(start)),
        where("endDate", "<=", Timestamp.fromDate(end)),
      ),
      (snapshot) => {
        const results = snapshot.docs.map((doc) => ({ 
          id: doc.id, 
          ...doc.data() 
        }) as Visit);
        setVisits(results);
        setLoadingVisits(false);
      },
      (error) => {
        console.error("Error fetching visits:", error);
        setError(error);
        setLoadingVisits(false);
      }
    );

    return () => unsub();
  }, [range, account?.id, refreshKey]);

  return {
    date,
    setDate,
    range,
    setRange,
    visits,
    filteredVisits,
    updatingEvent,
    setUpdatingEvent,
    loadingVisits,
    error,
    refresh,
  };
}; 